// The coin flip of a room, over the board: the die tumbles in and lands on a
// sun face or a moon face, and the words under it say which side this screen
// plays. See `state/coinFlip.ts`.

import { DIE_SPIN_MS, REDUCED_MOTION_QUERY } from '../constants';
import { DIE_FACES, type DieFaceLabel } from '../dice/octahedron';
import { OWNER_NAMES } from '../engine/describe';
import type { Side } from '../engine/types';
import { el } from '../lib/dom';
import { type CoinFlip, coinFlipStore } from '../state/coinFlip';
import { createTumblingDie } from './TumblingDie';

const TONE_ID_PREFIX = 'flip-die-tone-';
/** The faces take the two marks in turn, so each side has four. */
const FACE_SIDES: readonly Side[] = DIE_FACES.map((_, index) =>
  index % 2 === 0 ? 'gold' : 'violet',
);
const LABELS: readonly DieFaceLabel[] = FACE_SIDES.map((side) => ({ side }));

/** Plain words, not final copy. */
const headline = ({ side, spectator }: CoinFlip): string =>
  spectator ? `${OWNER_NAMES.gold} moves first` : `You are ${OWNER_NAMES[side]}`;
const detail = ({ side, spectator }: CoinFlip): string =>
  spectator ? '' : side === 'gold' ? 'You move first.' : `${OWNER_NAMES.gold} moves first.`;

/** `launchPad` is the die in the cue. The die of the flip enters from it, as a roll does. */
export const createCoinFlip = (launchPad: HTMLElement): HTMLElement => {
  const slot = el('div', { className: 'stage__die coin-flip__die' });
  const title = el('p', { className: 'coin-flip__title' });
  const line = el('p', { className: 'coin-flip__line' });
  const words = el('div', { className: 'coin-flip__words' }, [title, line]);
  const root = el('div', { className: 'coin-flip', hidden: true }, [slot, words]);
  const die = createTumblingDie(slot, launchPad, TONE_ID_PREFIX);
  die.label(LABELS);
  root.prepend(die.svg);
  let wordsTimer = 0;

  coinFlipStore.subscribe((flip) => {
    window.clearTimeout(wordsTimer);
    root.hidden = flip === null;
    words.hidden = true;
    if (!flip) {
      die.stop();
      return;
    }
    title.textContent = headline(flip);
    line.textContent = detail(flip);
    // A random face of the side, so the die does not land the same way each time.
    const faces = FACE_SIDES.flatMap((side, index) => (side === flip.side ? [index] : []));
    const face = faces[Math.floor(Math.random() * faces.length)] ?? 0;
    // The die measures the stage, so it starts after the stage shows.
    requestAnimationFrame(() => die.tumble(face));
    const spin = window.matchMedia(REDUCED_MOTION_QUERY).matches ? 0 : DIE_SPIN_MS;
    wordsTimer = window.setTimeout(() => {
      words.hidden = false;
    }, spin);
  });
  return root;
};
